import { Dropdown, Space } from 'antd';
import { camelCase, capitalize, get, keyBy } from 'lodash';
import naturalCompare from 'natural-compare-lite';
import React, { FC, useMemo } from 'react';
import styled from 'styled-components';
import { Button } from 'components/atoms/button';
import { Menu } from 'components/atoms/dropdown';
import { FieldType } from 'components/organisms/tasks/fields';
import { ProductiveHeading07 } from 'components/styles';
import { STATUS_FIELD_NAME } from 'data/fields/hooks';
import { useTranslation } from 'hooks/use-translation';
import {
  FIELD_ID_PROJECT_PHASE_FIELD,
  FIELD_ID_PROJECT_PHASE_FIELD_WITH_ID,
  TaskField,
} from 'stores/fields';

export interface Item {
  label: string;
  value: string;
  fieldId: number;
  isCustomField: boolean;
}

export const NONE = '-1';

const computeKanbanBoardRowItems = (
  fields: TaskField[],
  isAllTasksPage: boolean,
): Item[] => {
  return fields
    .filter(({ type, fieldId }) => {
      return (
        (type === FieldType.SingleSelect &&
          fieldId !== FIELD_ID_PROJECT_PHASE_FIELD_WITH_ID) ||
        type === FieldType.Assignee ||
        (!isAllTasksPage && fieldId === FIELD_ID_PROJECT_PHASE_FIELD)
      );
    })
    .filter(
      (field) =>
        ![camelCase(STATUS_FIELD_NAME), 'taskDependencyStatus'].includes(
          field.fieldName,
        ),
    )
    .map(({ fieldLabel, fieldName, fieldId, customField }) => {
      return {
        label: fieldLabel || capitalize(fieldName),
        value: fieldName,
        fieldId,
        isCustomField: customField || false,
      };
    })
    .sort(({ label: a }, { label: b }) => naturalCompare(a, b));
};

const computeKanbanBoardColumnItems = (fields: TaskField[]): Item[] => {
  return fields
    .filter(({ fieldName }) => {
      return ['status', 'priority', 'assignee', 'dueDate'].includes(fieldName);
    })
    .map(({ fieldLabel, fieldName, fieldId, customField }) => {
      return {
        label: fieldLabel || capitalize(fieldName),
        value: fieldName,
        fieldId,
        isCustomField: customField || false,
      };
    })
    .sort(({ label: a }, { label: b }) => naturalCompare(a, b));
};

export const useRowItems = (
  fields: TaskField[],
  isAllTasksPage = false,
): Item[] => {
  const none = useTranslation('none');

  return useMemo(() => {
    const result = [
      { label: none, value: NONE, fieldId: -1, isCustomField: false },
    ];
    const rowFields = computeKanbanBoardRowItems(fields, isAllTasksPage);

    return [...result, ...rowFields];
  }, [fields, isAllTasksPage, none]);
};

export const useColumnItems = (fields: TaskField[]): Item[] => {
  return useMemo(() => {
    return computeKanbanBoardColumnItems(fields);
  }, [fields]);
};

interface Props {
  items: Item[];
  onChange: (fieldName: string) => void;
  selected: string;
  label: React.ReactNode;
  icon: React.ReactNode;
  disabled?: boolean;
}

const Selected = styled.div`
  ${ProductiveHeading07};
`;

export const GroupByFilter: FC<Props> = ({
  items = [],
  onChange,
  selected,
  label,
  icon,
  disabled,
}) => {
  const hash = keyBy(items, 'value');
  const menu = (
    <Menu
      selectedKeys={[selected]}
      onClick={({ key }) => onChange(String(key))}
    >
      {items.map((item) => (
        <Menu.Item key={item.value}>{item.label}</Menu.Item>
      ))}
    </Menu>
  );

  return (
    <Dropdown overlay={menu}>
      <Button icon={icon} type="text" size="small" disabled={disabled}>
        <Space align="center" size={4}>
          <div>{label}:</div>
          <Selected>{get(hash, [selected, 'label'])}</Selected>
        </Space>
      </Button>
    </Dropdown>
  );
};

export const GroupByColumnFilter = GroupByFilter;
